"use client"

import { useState, useEffect } from "react"
import { ExternalLink, Loader2 } from "lucide-react"
import { getUrlMetadata, getEmbedType, getYouTubeVideoId } from "../../lib/url-utils"

export function LinkPreview({ url, isExpanded = false }) {
  const [metadata, setMetadata] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const embedType = getEmbedType(url)

  useEffect(() => {
    async function fetchMetadata() {
      try {
        const data = await getUrlMetadata(url)
        setMetadata(data)
      } catch (err) {
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    fetchMetadata()
  }, [url])

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading preview...
      </div>
    )
  }

  if (error || !metadata) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-600 dark:text-blue-400"
      >
        {url}
        <ExternalLink className="w-4 h-4" />
      </a>
    )
  }

  if (!isExpanded) {
    return (
      <div className="max-w-sm rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:border-gray-300 dark:hover:border-gray-700 transition-colors">
        {metadata.image && (
          <img
            src={metadata.image || "/placeholder.svg"}
            alt={metadata.title}
            className="w-full h-32 object-cover bg-gray-100 dark:bg-gray-800"
          />
        )}
        <div className="p-4">
          <h3 className="font-medium text-gray-900 dark:text-gray-100 line-clamp-1">{metadata.title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">{metadata.description}</p>
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
            {metadata.siteName && <span>{metadata.siteName}</span>}
            <ExternalLink className="w-4 h-4" />
          </div>
        </div>
      </div>
    )
  }

  switch (embedType) {
    case "youtube": {
      const videoId = getYouTubeVideoId(url)
      if (!videoId) return null

      return (
        <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      )
    }

    case "twitter":
      return (
        <div className="twitter-embed" data-url={url}>
          {/* Twitter embed will be handled by external script */}
          <blockquote className="twitter-tweet">
            <a href={url}>Loading tweet...</a>
          </blockquote>
        </div>
      )

    case "spotify":
      return (
        <div className="spotify-embed rounded-xl overflow-hidden">
          <iframe
            src={url.replace("/track/", "/embed/track/")}
            width="100%"
            height="80"
            frameBorder="0"
            allow="encrypted-media"
          />
        </div>
      )

    case "image":
      return (
        <div className="rounded-xl overflow-hidden">
          <img src={url || "/placeholder.svg"} alt="Shared content" className="max-w-full h-auto" />
        </div>
      )

    case "video":
      return (
        <div className="rounded-xl overflow-hidden">
          <video src={url} controls className="max-w-full" />
        </div>
      )

    default:
      return (
        <div className="max-w-2xl rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:border-gray-300 dark:hover:border-gray-700 transition-colors">
          {metadata.image && (
            <img
              src={metadata.image || "/placeholder.svg"}
              alt={metadata.title}
              className="w-full h-48 object-cover bg-gray-100 dark:bg-gray-800"
            />
          )}
          <div className="p-4">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">{metadata.title}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{metadata.description}</p>
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
              {metadata.siteName && <span>{metadata.siteName}</span>}
              <ExternalLink className="w-4 h-4" />
            </div>
          </div>
        </div>
      )
  }
}
